/**
 * ArrayNode - stores and outputs array patterns with interactive editing
 *
 * Endpoints:
 * - preset: dropdown to select factory presets (Sine, Ramp, Steps, etc.)
 * - length: array length (1-256)
 * - min/max: value range for clamping and visualization
 * - step: quantization step for visualization snapping
 * - pattern (input/output): the array data
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { Preset } from '@/nodes/PresetManager';
import { params } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Presets
// ─────────────────────────────────────────────────────────────

const ARRAY_PRESETS: Preset[] = [
  {
    name: 'Sine',
    params: {
      length: 16, min: { value: -1 }, max: { value: 1 }, step: { value: 0.01 },
      pattern: Array.from({ length: 16 }, (_, i) => +(Math.sin(i / 16 * Math.PI * 2)).toFixed(3)),
    }
  },
  {
    name: 'Ramp',
    params: {
      length: 8, min: { value: 0 }, max: { value: 1 }, step: { value: 0.01 },
      pattern: Array.from({ length: 8 }, (_, i) => +(i / 7).toFixed(3)),
    }
  },
  {
    name: 'Ease In-Out',
    params: {
      length: 16, min: { value: 0 }, max: { value: 1 }, step: { value: 0.01 },
      pattern: Array.from({ length: 16 }, (_, i) => {
        const t = i / 15;
        return +(t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2).toFixed(3);
      }),
    }
  },
  {
    name: 'Steps',
    params: {
      length: 8, min: { value: 0 }, max: { value: 100 }, step: { value: 20 },
      pattern: [0, 20, 40, 60, 80, 60, 40, 20],
    }
  },
  {
    name: 'Indices',
    params: {
      length: 8, min: { value: 0 }, max: { value: 7 }, step: { value: 1 },
      pattern: [0, 1, 2, 3, 4, 5, 6, 7],
    }
  },
];

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const endpoints = params({
  preset: { dataType: 'string', options: ARRAY_PRESETS.map(p => p.name), value: ARRAY_PRESETS[0].name },
  length: { value: 16, min: 1, max: 256, step: 1, label: 'Length' },
  min: { value: 0, min: 0, max: 100, step: 1, label: 'Min' },
  max: { value: 10, min: 0, max: 100, step: 1, label: 'Max' },
  step: { value: 1, min: 1, max: 6, step: 1, label: 'Step' },
  pattern: { dataType: 'array', min:0, max:10, length:16,step:1, isOutput: true, primary: true, value: [] as number[] },
});

export class ArrayNode extends BaseNode<typeof endpoints> {
  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    // Generate initial pattern from first preset if pattern is empty
    const pattern = this.params.pattern.value;
    if (!Array.isArray(pattern) || pattern.length === 0) {
      const preset = ARRAY_PRESETS[0];
      this.params.pattern.set(preset.params.pattern);
    } else {
      this.params.pattern.set(pattern);
    }
    this.syncPatternMetadata();
  }

  onParamChanged(_payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'length': {
        const len = this.params.length.value;
        const current = this.params.pattern.value as number[];
        const min = this.params.min.value;
        if (current.length !== len) {
          const resized = current.length > len
            ? current.slice(0, len)
            : [...current, ...Array(len - current.length).fill(min)];
          this.params.pattern.set(resized);
        }
        this.syncPatternMetadata();
        break;
      }
      case 'min':
      case 'max': {
        const lo = this.params.min.value;
        const hi = this.params.max.value;
        const current = this.params.pattern.value as number[];
        const clamped = current.map(v => Math.min(hi, Math.max(lo, v)));
        this.params.pattern.set(clamped);
        this.syncPatternMetadata();
        break;
      }
      case 'step':
        this.syncPatternMetadata();
        break;
      // preset: handled by runtime preset loading
      // pattern: I/O endpoint, auto-propagates
    }
  }

  /** Copy min/max/step/length onto the pattern endpoint metadata so TweakControl can render ArraySliderControl */
  private syncPatternMetadata(): void {
    this.setEndpointMetadata('pattern', {
      min: this.params.min.value,
      max: this.params.max.value,
      step: this.params.step.value,
      length: this.params.length.value,
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const arrayNodeDef: NodeDef<ArrayNode> = {
  type: 'Array',
  shortName: 'arr',
  info: 'Interactive array pattern editor with factory presets (sine, ramp, steps, etc.).',
  domain: Domain.Data,
  endpoints,
  factoryPresets: ARRAY_PRESETS,
  ctor: ArrayNode
};
